import type { ReactNode } from "react";
import { Users } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useEntitlement } from "@/contexts/EntitlementContext";
import { UpgradeBody } from "@/components/billing/Upgrade";

/**
 * Entitlement gate for the Teams surface, mirroring AnalyticsGate. The /teams routes
 * are open (auth + `teams` flag only); this renders the real view for entitled users
 * (Team plan and above) and ONLY an upgrade CTA for everyone else — so direct
 * navigation never exposes the feature, and the view's chunk isn't loaded for
 * unentitled viewers (the children are lazy and only rendered when entitled).
 *
 * Checked after the entitlement has resolved (loading → render nothing) to avoid a
 * flash of the upgrade panel for entitled users while the cached fetch loads.
 */
export function TeamsGate({ children }: { children: ReactNode }) {
  const { can, loading } = useEntitlement();

  if (loading) return null;
  if (can("teams")) return <>{children}</>;

  return (
    <div className="min-h-full">
      <PageHeader
        title="Teams"
        breadcrumb="Share workspaces and sessions with your team"
        actions={
          <span className="text-xs font-semibold uppercase tracking-wide text-brand">
            Team
          </span>
        }
      />
      <div className="mx-auto max-w-3xl px-6 py-8">
        <div className="flex items-start gap-3">
          <Users size={20} className="mt-0.5 text-brand flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold tracking-tight text-foreground text-balance">
              Bring your team into SpecStory
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              Create a team to share your workspaces and sessions with
              teammates, invite members by email, and manage roles.
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-lg border border-border bg-card p-4 shadow-sm">
          <UpgradeBody feature="teams" />
        </div>
      </div>
    </div>
  );
}
